import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  EmptyState,
  LoadingState,
  MetricCard,
  PageHeader,
  SectionCard,
  StatusChip,
  useFeedback,
} from '../components/ui';
import { UserContext } from '../contexts/UserContext';
import { canSeeManagerNavigation, readSessionUser, roleLabel } from '../utils/sessionUser';

import { API_BASE_URL } from '../config/apiBase';
import { formatEventSchedule } from '../utils/hackathonFlow';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function geofenceSummary(org) {
  const latBlank = isBlank(org.latitude);
  const lngBlank = isBlank(org.longitude);
  const radiusBlank = isBlank(org.radius_m);
  if (latBlank && lngBlank && radiusBlank) return 'Raio não configurado';
  if (!latBlank && !lngBlank && !radiusBlank) return `Raio ${org.radius_m} m`;
  return 'Cerca incompleta';
}

function statusLabel(status) {
  // Rótulos visíveis da fila e do histórico.
  if (status === 'pendente') return 'Pendente';
  if (status === 'aprovado') return 'Aprovado';
  if (status === 'rejeitado') return 'Rejeitado';
  return status || '—';
}

function outcome(result) {
  if (result.status === 'fulfilled') return { data: result.value.data, status: 'ready' };
  if (result.reason?.response?.status === 403) return { data: null, status: 'forbidden' };
  return { data: null, status: 'error' };
}

function DashboardAdmin() {
  const { notify } = useFeedback();
  const { users, loading: usersLoading, usersError } = useContext(UserContext);
  const session = readSessionUser();
  const managerNav = canSeeManagerNavigation(session);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({ data: [], status: 'loading' });
  const [history, setHistory] = useState({ data: [], status: 'loading' });
  const [organizations, setOrganizations] = useState({ data: [], status: 'loading' });
  const [events, setEvents] = useState({ data: [], status: managerNav ? 'loading' : 'forbidden' });

  useEffect(() => {
    let active = true;
    const headers = { headers: authHeaders() };
    const requests = [
      axios.get(`${API_BASE_URL}/ponto/pendentes`, headers),
      axios.get(`${API_BASE_URL}/ponto/historico`, headers),
      axios.get(`${API_BASE_URL}/organizations`, headers),
    ];
    if (managerNav) {
      requests.push(axios.get(`${API_BASE_URL}/events`, headers));
    }

    Promise.allSettled(requests).then((results) => {
      if (!active) return;
      const [pendingResult, historyResult, orgResult, eventResult] = results;
      const nextPending = outcome(pendingResult);
      const nextHistory = outcome(historyResult);
      const nextOrgs = outcome(orgResult);
      const nextEvents = eventResult ? outcome(eventResult) : { data: null, status: 'forbidden' };
      setPending({ data: Array.isArray(nextPending.data) ? nextPending.data : [], status: nextPending.status });
      setHistory({ data: Array.isArray(nextHistory.data) ? nextHistory.data : [], status: nextHistory.status });
      setOrganizations({ data: Array.isArray(nextOrgs.data) ? nextOrgs.data : [], status: nextOrgs.status });
      setEvents({
        data: Array.isArray(nextEvents.data) ? nextEvents.data : [],
        status: nextEvents.status,
      });
      setLoading(false);

      const failures = [];
      if (nextPending.status === 'error') failures.push('pendências');
      if (nextHistory.status === 'error') failures.push('registros de ponto');
      if (nextOrgs.status === 'error') failures.push('organizações');
      if (nextEvents.status === 'error') failures.push('eventos');
      if (failures.length) {
        notify(`Não foi possível carregar: ${failures.join(', ')}.`, 'error');
      }
    });

    return () => {
      active = false;
    };
  }, [managerNav, notify]);

  const title = session?.tipo === 'funcionario' ? 'Meu painel' : 'Painel administrativo';
  const subtitle = session?.tipo === 'funcionario'
    ? 'Seus registros de ponto e as pendências da sua conta.'
    : 'Indicadores, pendências e atalhos com os dados já registrados.';

  const shortcuts = useMemo(() => {
    const items = [
      { label: 'Registrar ponto', to: '/ponto' },
      { label: 'Aprovar pontos', to: '/point-approval' },
    ];
    if (managerNav) {
      items.push(
        { label: 'Gerenciar usuários', to: '/users' },
        { label: 'Organizações', to: '/organizations' },
        { label: 'Eventos', to: '/events' },
        { label: 'Relatórios', to: '/reports' },
      );
    }
    return items;
  }, [managerNav]);

  const showUsers = managerNav && usersError !== 'forbidden';
  const userValue = usersLoading ? '…' : usersError === 'error' ? '—' : users.length;
  const userHint = usersError === 'error' ? 'Falha ao carregar' : 'Contas ativas';

  const pendingValue = pending.status === 'ready' ? pending.data.length : pending.status === 'error' ? '—' : '…';
  const eventValue = events.status === 'ready' ? events.data.length : events.status === 'error' ? '—' : '…';
  const orgValue = organizations.status === 'ready' ? organizations.data.length : organizations.status === 'error' ? '—' : '…';
  const orgHint = organizations.status === 'ready' && organizations.data.length === 1
    ? geofenceSummary(organizations.data[0])
    : organizations.status === 'error'
      ? 'Falha ao carregar'
      : 'Disponíveis para a conta';

  if (loading) {
    return <LoadingState label="Carregando painel" />;
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={(
          <>
            <Button component={RouterLink} to="/ponto" variant="contained">
              Registrar ponto
            </Button>
            {managerNav ? (
              <Button component={RouterLink} to="/users" state={{ create: true }} variant="outlined">
                Adicionar usuário
              </Button>
            ) : null}
          </>
        )}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: showUsers && events.status !== 'forbidden' ? 'repeat(4, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
          },
          gap: 2,
          mb: 3,
        }}
      >
        {showUsers ? <MetricCard label="Usuários cadastrados" value={userValue} hint={userHint} /> : null}
        <MetricCard
          label="Pontos pendentes"
          value={pendingValue}
          hint={pending.status === 'error' ? 'Falha ao carregar' : 'Fila atual'}
        />
        {events.status !== 'forbidden' ? (
          <MetricCard
            label="Eventos cadastrados"
            value={eventValue}
            hint={events.status === 'error' ? 'Falha ao carregar' : 'Lista de eventos'}
          />
        ) : null}
        <MetricCard label="Organizações" value={orgValue} hint={orgHint} />
      </Box>

      <SectionCard title="Atalhos" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {shortcuts.map((item) => (
            <Button key={item.to} component={RouterLink} to={item.to} variant="outlined" size="small">
              {item.label}
            </Button>
          ))}
        </Box>
      </SectionCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(0, 1fr)' }, gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <SectionCard
            title="Pendências"
            action={(
              <Button component={RouterLink} to="/point-approval" size="small">
                Abrir fila
              </Button>
            )}
          >
            <RecordList
              status={pending.status}
              items={pending.data.slice(0, 5)}
              emptyTitle="Nenhum ponto pendente no momento."
              renderItem={(item) => ({
                key: item.id,
                primary: item.nome_usuario || 'Seu ponto',
                secondary: `${item.tipo === 'saida' ? 'Saída' : 'Entrada'} · ${formatDateTime(item.data_hora)}`,
                status: item.status || 'pendente',
              })}
            />
          </SectionCard>

          <SectionCard title="Seus registros recentes">
            <RecordList
              status={history.status}
              items={history.data.slice(0, 5)}
              emptyTitle="Nenhum registro de ponto na sua conta."
              renderItem={(item) => ({
                key: item.id,
                primary: item.tipo === 'saida' ? 'Saída' : 'Entrada',
                secondary: formatDateTime(item.data_hora),
                status: item.status,
              })}
            />
          </SectionCard>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {events.status !== 'forbidden' ? (
            <SectionCard
              title="Eventos"
              action={(
                <Button component={RouterLink} to="/events" size="small">
                  Ver todos
                </Button>
              )}
            >
              {events.status === 'error' ? (
                <Typography variant="body2" color="text.secondary">Não foi possível carregar os eventos.</Typography>
              ) : events.data.length === 0 ? (
                <EmptyState title="Nenhum evento cadastrado." />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {events.data.slice(0, 3).map((event) => (
                    <Box key={event.id} sx={{ minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{event.nome}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatEventSchedule(event.start_datetime)}
                        {event.end_datetime ? ` – ${formatEventSchedule(event.end_datetime)}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {`Participantes: ${event.total_participantes ?? 0} · Subeventos: ${event.total_sub_events ?? 0}`}
                        {isBlank(event.radius_m) ? '' : ` · Raio ${event.radius_m} m`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </SectionCard>
          ) : null}

          <SectionCard
            title="Organização"
            action={managerNav ? (
              <Button component={RouterLink} to="/organizations" size="small">
                Gerenciar
              </Button>
            ) : null}
          >
            {organizations.status === 'error' ? (
              <Typography variant="body2" color="text.secondary">Não foi possível carregar as organizações.</Typography>
            ) : organizations.data.length === 0 ? (
              <EmptyState title="Nenhuma organização disponível para esta conta." />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {organizations.data.map((org) => (
                  <Box key={org.id} sx={{ minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{org.nome}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {org.cnpj ? `CNPJ ${org.cnpj}` : 'CNPJ não informado'}
                    </Typography>
                    {org.email ? (
                      <Typography variant="body2" color="text.secondary">{org.email}</Typography>
                    ) : null}
                    {org.endereco ? (
                      <Typography variant="body2" color="text.secondary">{org.endereco}</Typography>
                    ) : null}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{geofenceSummary(org)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {session?.tipo ? `Perfil ${roleLabel(session.tipo)}.` : 'Sessão autenticada.'}
        {' '}A atividade recente mostra apenas os pontos da conta logada.
      </Typography>
    </Box>
  );
}

function RecordList({ status, items, emptyTitle, renderItem }) {
  if (status === 'error') {
    return <Typography variant="body2" color="text.secondary">Não foi possível carregar esta lista.</Typography>;
  }
  if (!items.length) {
    return <EmptyState title={emptyTitle} />;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item) => {
        const row = renderItem(item);
        return (
          <Box key={row.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.primary}</Typography>
              <Typography variant="caption" color="text.secondary">{row.secondary}</Typography>
            </Box>
            <StatusChip status={row.status} label={statusLabel(row.status)} />
          </Box>
        );
      })}
    </Box>
  );
}

export default DashboardAdmin;
